import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

export function AuthRedirect() {
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Check if we have the full confirmation URL (new method)
    const confirmationUrl = searchParams.get('confirmation_url');
    
    if (confirmationUrl) {
      // New method: extract token from Supabase confirmation URL
      try {
        const supabaseUrl = new URL(confirmationUrl);
        const token = supabaseUrl.searchParams.get('token');
        const tokenHash = supabaseUrl.searchParams.get('token_hash');
        const type = supabaseUrl.searchParams.get('type');
        
        // Use the real token from Supabase URL
        const actualToken = tokenHash || token;
        
        if (!actualToken) {
          throw new Error('No token found in confirmation URL');
        }
        
        // Update the redirect_to to point to our domain
        supabaseUrl.searchParams.set('redirect_to', `${window.location.origin}/auth/verify`);
        
        console.log('Redirecting to Supabase with extracted token:', {
          token: actualToken.substring(0, 10) + '...',
          type,
          originalUrl: confirmationUrl
        });
        
        // Redirect to the corrected Supabase URL
        window.location.href = supabaseUrl.toString();
        return;
        
      } catch (error) {
        console.error('Error parsing confirmation URL:', error);
        setError('Invalid confirmation URL format');
        setTimeout(() => {
          window.location.href = '/auth/login?error=invalid_confirmation_url';
        }, 3000);
        return;
      }
    }

    // Fallback to old method for backward compatibility
    const token = searchParams.get('token');
    const type = searchParams.get('type');
    const redirectTo = searchParams.get('redirect_to') || `${window.location.origin}/auth/verify`;

    // Validate required parameters
    if (!token) {
      setError('Missing verification token');
      setTimeout(() => {
        window.location.href = '/auth/login?error=missing_token';
      }, 3000);
      return;
    }

    if (!type) {
      setError('Missing verification type');
      setTimeout(() => {
        window.location.href = '/auth/login?error=missing_type';
      }, 3000);
      return;
    }

    // Validate token format (basic check) - made more lenient for different token types
    if (token.length < 4) {
      setError('Invalid token format');
      setTimeout(() => {
        window.location.href = '/auth/login?error=invalid_token';
      }, 3000);
      return;
    }

    // Validate type is expected value
    const validTypes = ['signup', 'email_change', 'recovery', 'email'];
    if (!validTypes.includes(type)) {
      setError('Invalid verification type');
      setTimeout(() => {
        window.location.href = '/auth/login?error=invalid_type';
      }, 3000);
      return;
    }

    try {
      // Construct the Supabase verification URL
      const supabaseUrl = `https://uyanvkwkikftzpanolmw.supabase.co/auth/v1/verify?token=${encodeURIComponent(token)}&type=${encodeURIComponent(type)}&redirect_to=${encodeURIComponent(redirectTo)}`;
      
      // Redirect to Supabase for verification
      window.location.href = supabaseUrl;
    } catch {
      setError('Failed to process verification link');
      setTimeout(() => {
        window.location.href = '/auth/login?error=processing_failed';
      }, 3000);
    }
  }, [searchParams]);

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-64px)] bg-gray-100 px-4">
        <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
            <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Verification Error</h1>
          <p className="text-gray-600 mb-4">{error}</p>
          <p className="text-sm text-gray-500">Redirecting you to login...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-[calc(100vh-64px)] bg-gray-100 px-4">
      <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-wayja-teal mx-auto mb-4"></div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Verifying Your Email</h1>
        <p className="text-gray-600">Please wait while we verify your email address...</p>
      </div>
    </div>
  );
} 